import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useStickyState } from "@/lib/useStickyState";
import { Link, Redirect } from "wouter";
import {
  Box,
  Boxes,
  Check,
  ChevronDown,
  ChevronLeft,
  Image as ImageIcon,
  Loader2,
  Music,
  Pause,
  Pencil,
  Play,
  Search,
  Sparkles,
  Trash2,
  TreePine,
  Upload,
  Volume2,
  Wrench,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  type Asset,
  type AssetKind,
  type AssetStatus,
  type SfxSlot,
  SFX_SLOTS,
  useAdminAssets,
  useAssetStatus,
  useDeleteAsset,
  useGenerateAsset,
  useGenerateSfx,
  useMe,
  useUpdateAsset,
  useUploadAsset,
} from "@/lib/queries";
import { invalidateSfxCatalog } from "@/lib/sfx-catalog";
import { previewSlotSynth } from "@/lib/feedback";
import { ApiError } from "@/lib/api";
import {
  AmbientLight,
  Color,
  DirectionalLight,
  DoubleSide,
  HemisphereLight,
  Mesh,
  MeshBasicMaterial,
  PerspectiveCamera,
  PlaneGeometry,
  Scene,
  WebGLRenderer,
} from "three";
import {
  createBoard,
  dimsFromAsset,
  disposeObject,
  loadImageTexture,
  type BoardHandle,
} from "@/lib/virtual3d";
import { REFERENCE_BOARD_TEMPLATE } from "@/lib/virtual3d-assets";

// Unified asset designer. One page replaces the three throwaway
// /admin/{scenes,boards,bags} pages. Tabs filter by kind; the same
// generate → review → activate workflow applies to all three.
//
// Built-in presets land here too with an "Built-in" badge so admins
// can deactivate them (e.g. retire the duck-pink bag) without code
// changes. Built-ins can't be hard-deleted — they'd just reseed on the
// next deploy — they can only be flipped to status=inactive.

const KIND_TABS: Array<{
  id: AssetKind;
  label: string;
  Icon: typeof TreePine;
  hint: string;
}> = [
  { id: "scene", label: "Scenes", Icon: TreePine, hint: "Backyard, neon arena…" },
  { id: "board", label: "Boards", Icon: Boxes, hint: "Top-down deck surfaces." },
  { id: "bag", label: "Bags", Icon: Box, hint: "Photoreal cornhole bags." },
  { id: "sfx", label: "Sound FX", Icon: Volume2, hint: "Fanfares, dings, thuds." },
  { id: "music", label: "Music", Icon: Music, hint: "Background playlist tracks." },
];

// Human labels for each named SFX slot. Kept here (not in the DB
// schema) because the strings are UI-only.
const SFX_SLOT_LABELS: Record<SfxSlot, string> = {
  round_start: "Round start fanfare",
  round_winner: "Round winner ding",
  throw_release: "Bag thrown (release)",
  bag_land: "Bag lands on board",
  bag_in_hole: "Bag in the hole",
  bag_on_ground: "Bag on the lawn (miss)",
  four_bagger: "4 Bagger! (12 in a round)",
  game_win: "Game won",
};

const STATUS_TABS: Array<{ id: AssetStatus | "all"; label: string }> = [
  { id: "all", label: "All" },
  { id: "active", label: "Active" },
  { id: "draft", label: "Drafts" },
  { id: "inactive", label: "Inactive" },
];

// Default prompts describe ONLY what's left to the user — the renderer
// orientation, tileability, and "no sky / no perspective" framing all
// come from the recipe pre-pended server-side. Keep these focused on
// the *material* the admin wants.
const DEFAULT_PROMPTS: Record<AssetKind, string> = {
  // Scene image becomes the 360° panoramic backdrop around the
  // playing field. Describe the surroundings (BBQ, fence, sky,
  // horizon, time of day) — not the lawn / floor surface.
  scene:
    "Sunny suburban backyard mid-afternoon, wooden fence in the background, a brick patio with BBQ and lawn chairs on one side, a few leafy trees, clear blue sky with light clouds",
  // Boards: full visual treatment. Renderer respects portrait framing.
  board:
    "Light wood grain sporty cornhole board top with the hole drawn in 3D black shadow at the back/top and a triangular helper line pointing toward the hole below it",
  // Bags: just the cloth / pattern. Asset is rendered on both top and
  // bottom face.
  bag: "Classic red duckcloth corn-filled bag with double-stitched cream seam",
  // SFX: text → sound effect via ElevenLabs. Empty by default so the
  // admin always types their OWN description (a non-empty default got
  // generated verbatim when people typed into the wrong field). The
  // textarea placeholder shows an example instead.
  sfx: "",
  music: "",
};

export default function AdminDesign() {
  const { data: me, isLoading } = useMe();
  const [kind, setKind] = useState<AssetKind>("scene");
  const [statusFilter, setStatusFilter] = useState<AssetStatus | "all">("all");
  const [search, setSearch] = useState("");

  if (isLoading) {
    return <div className="p-8 text-muted-foreground text-sm">Loading…</div>;
  }
  if (!me) return <Redirect to="/sign-in" />;
  if (!me.isPlatformAdmin) {
    return (
      <div className="p-8 max-w-md mx-auto text-center space-y-3">
        <Wrench className="mx-auto text-muted-foreground" size={32} />
        <h1 className="text-2xl font-display uppercase tracking-tight">
          Restricted
        </h1>
        <p className="text-sm text-muted-foreground">
          The asset designer is platform-admin only.
        </p>
        <Button asChild>
          <Link href="/dashboard">Back to dashboard</Link>
        </Button>
      </div>
    );
  }

  return (
    <article className="px-4 sm:px-8 py-6 sm:py-10 max-w-5xl mx-auto space-y-6">
      <header className="space-y-1">
        <div>
          <Link
            href="/admin"
            className="text-xs font-display uppercase tracking-widest text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
          >
            <ChevronLeft size={12} aria-hidden /> Admin
          </Link>
        </div>
        <p className="text-xs font-display uppercase tracking-widest text-muted-foreground">
          /admin/design
        </p>
        <h1 className="text-3xl font-display uppercase tracking-tight inline-flex items-center gap-2">
          <Sparkles className="text-primary" /> Asset designer
        </h1>
        <p className="text-muted-foreground text-sm">
          Generate, catalog, and assign every visual swappable in the
          virtual cornhole game. New generations land as drafts;
          flip status to <em>active</em> to expose them in the in-game picker.
        </p>
      </header>

      <ConfigStatus />

      <KindTabs value={kind} onChange={setKind} />

      {kind === "music" ? (
        <MusicUploadPanel />
      ) : (
        <>
          <GeneratePanel kind={kind} />
          {kind === "sfx" && <GameSoundsPanel />}
        </>
      )}

      <CatalogPanel
        kind={kind}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        search={search}
        setSearch={setSearch}
      />
    </article>
  );
}

function ConfigStatus() {
  const { data } = useAssetStatus();
  if (!data) return null;
  if (data.hasGeminiKey && data.hasR2) return null;
  const missing: string[] = [];
  if (!data.hasGeminiKey) missing.push("GEMINI_API_KEY");
  if (!data.hasR2)
    missing.push(
      "R2_ACCOUNT_ID, R2_BUCKET, R2_ACCESS_KEY_ID, R2_SECRET_ACCESS_KEY, R2_PUBLIC_BASE_URL",
    );
  return (
    <Card className="border-destructive/40 bg-destructive/5">
      <CardContent className="p-4 text-sm">
        <p className="font-medium text-destructive">Asset designer not fully configured</p>
        <p className="text-muted-foreground mt-1">
          Missing env: <code className="bg-muted px-1 py-0.5 rounded">{missing.join(", ")}</code>.
          Set on Fly with{" "}
          <code className="bg-muted px-1 py-0.5 rounded">flyctl secrets set</code>.
        </p>
      </CardContent>
    </Card>
  );
}

// Background-music uploader. Music is upload-only audio (no generation),
// so it gets a dedicated simple panel instead of the generate machinery.
// Active tracks play as a looping playlist on the live scorer.
function MusicUploadPanel() {
  const upload = useUploadAsset();
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const { data: status } = useAssetStatus();
  const canUpload = status?.hasR2 ?? false;

  async function onFile(file: File) {
    setError(null);
    const reader = new FileReader();
    const dataUrl = await new Promise<string | null>((resolve) => {
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => {
        setError(reader.error?.message ?? "Could not read file");
        resolve(null);
      };
      reader.readAsDataURL(file);
    });
    if (!dataUrl) return;
    const effName =
      name.trim() ||
      file.name.replace(/\.[a-z0-9]+$/i, "").slice(0, 80) ||
      "Untitled";
    try {
      await upload.mutateAsync({ kind: "music", name: effName, dataUrl });
      setName("");
      if (fileRef.current) fileRef.current.value = "";
      qc.invalidateQueries({ queryKey: ["assets", "catalog", "music"] });
    } catch (e) {
      setError((e as ApiError).message ?? "Upload failed");
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="inline-flex items-center gap-2">
          <Music size={16} className="text-primary" /> Background music
        </CardTitle>
        <CardDescription>
          Upload tracks (MP3/OGG/WAV). Active tracks play as a looping
          playlist on the live scorer and automatically dip while the AI
          announcer talks. Uploads land as drafts — flip to <em>active</em>{" "}
          below to add them to the playlist.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Input
          placeholder="Track name (optional — defaults to the file name)"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <input
          ref={fileRef}
          type="file"
          accept="audio/*"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void onFile(f);
          }}
        />
        <Button
          onClick={() => fileRef.current?.click()}
          disabled={upload.isPending || !canUpload}
        >
          {upload.isPending ? (
            <>
              <Loader2 size={14} className="animate-spin" /> Uploading…
            </>
          ) : (
            <>
              <Upload size={14} /> Upload track
            </>
          )}
        </Button>
        {!canUpload && status ? (
          <p className="text-xs text-muted-foreground">
            Uploads need R2 configured on the server.
          </p>
        ) : null}
        {error ? <p className="text-sm text-destructive">{error}</p> : null}
      </CardContent>
    </Card>
  );
}

// Slot-centric "Game Sounds" board. Lists every game-sound EVENT (not
// every uploaded file) so an admin sees, at a glance, what the game
// sounds like and can change any of it: preview the built-in default,
// preview the currently-bound clip, and assign which uploaded clip
// plays for that event (or revert to Default). Complements the
// asset-centric upload list below.
function GameSoundsPanel() {
  const { data: assets = [] } = useAdminAssets({ kind: "sfx" });
  const upd = useUpdateAsset();
  const [err, setErr] = useState<string | null>(null);
  // Only active clips are assignable — the in-game catalog plays active
  // rows only, so binding a draft would silently do nothing.
  const clips = assets.filter((a) => a.status === "active");

  // Add a clip to a slot's POOL (set its slug). A clip plays for ONE
  // event, so this moves it out of any other pool it was in.
  async function addToPool(slot: SfxSlot, assetId: string) {
    setErr(null);
    try {
      await upd.mutateAsync({ id: assetId, slug: slot });
      invalidateSfxCatalog();
    } catch (e) {
      setErr((e as ApiError).message ?? "Could not add to pool");
    }
  }
  // Remove a clip from its pool (clear slug → falls back toward the
  // remaining pool, or the built-in default if the pool empties).
  async function removeFromPool(assetId: string) {
    setErr(null);
    try {
      await upd.mutateAsync({ id: assetId, slug: null });
      invalidateSfxCatalog();
    } catch (e) {
      setErr((e as ApiError).message ?? "Could not remove from pool");
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base inline-flex items-center gap-2">
          <Volume2 size={16} className="text-primary" /> Game sounds
        </CardTitle>
        <CardDescription>
          The seven sounds the game plays. Assign one or MORE clips to an
          event to build a pool — the game picks one at random each time
          it fires, for variety. An empty pool falls back to the built-in
          default. (A clip plays for one event; adding it elsewhere moves
          it.)
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        <ul className="divide-y divide-border">
          {SFX_SLOTS.map((slot) => {
            const pool = clips.filter((a) => a.slug === slot);
            const addable = clips.filter((a) => a.slug !== slot);
            return (
              <li key={slot} className="p-3 sm:p-4 space-y-2">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                  <div className="flex-1 min-w-[10rem]">
                    <div className="font-medium text-sm">
                      {SFX_SLOT_LABELS[slot]}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      <code className="bg-muted/60 px-1 py-0.5 rounded mr-2">
                        {slot}
                      </code>
                      {pool.length === 0
                        ? "built-in default"
                        : pool.length === 1
                          ? "1 clip"
                          : `${pool.length} clips — randomized`}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => previewSlotSynth(slot)}
                    className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md border border-border hover:bg-muted/50 text-xs"
                    title="Preview the built-in default sound"
                  >
                    <Play size={12} /> Default
                  </button>
                  {addable.length > 0 && (
                    <select
                      value=""
                      onChange={(e) => {
                        if (e.target.value) void addToPool(slot, e.target.value);
                        e.currentTarget.value = "";
                      }}
                      disabled={upd.isPending}
                      className="h-8 rounded-md border border-input bg-transparent px-2 text-xs disabled:opacity-60 max-w-[12rem]"
                      aria-label={`Add a clip to ${SFX_SLOT_LABELS[slot]}`}
                    >
                      <option value="">+ Add clip…</option>
                      {addable.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                          {c.slug ? ` (moves from ${c.slug})` : ""}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                {pool.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {pool.map((c) => (
                      <span
                        key={c.id}
                        className="inline-flex items-center gap-1.5 rounded-full bg-muted/50 border border-border pl-1 pr-2 py-0.5 text-xs"
                      >
                        {c.imageUrl && <ClipPreviewButton url={c.imageUrl} />}
                        <span className="truncate max-w-[10rem]">{c.name}</span>
                        <button
                          type="button"
                          onClick={() => removeFromPool(c.id)}
                          disabled={upd.isPending}
                          className="text-muted-foreground hover:text-destructive disabled:opacity-50"
                          title="Remove from this event"
                          aria-label={`Remove ${c.name} from ${SFX_SLOT_LABELS[slot]}`}
                        >
                          <X size={12} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
        {clips.length === 0 && (
          <p className="px-4 pb-4 text-xs text-muted-foreground">
            No active clips yet — every event uses its built-in default
            (tap “Default” to hear them). Generate or upload clips above,
            flip them to <em>active</em>, then add them to an event here.
          </p>
        )}
        {err && <p className="px-4 pb-3 text-xs text-destructive">{err}</p>}
      </CardContent>
    </Card>
  );
}

// Compact play/stop button for auditioning a bound clip inline in the
// Game Sounds rows. Lazy-builds the Audio on first tap.
function ClipPreviewButton({ url }: { url: string }) {
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  function toggle() {
    if (!audioRef.current) {
      audioRef.current = new Audio(url);
      audioRef.current.addEventListener("ended", () => setPlaying(false));
      audioRef.current.addEventListener("pause", () => setPlaying(false));
      audioRef.current.addEventListener("play", () => setPlaying(true));
    }
    const a = audioRef.current;
    if (playing) {
      a.pause();
    } else {
      a.currentTime = 0;
      void a.play().catch(() => setPlaying(false));
    }
  }
  useEffect(() => {
    return () => {
      audioRef.current?.pause();
      audioRef.current = null;
    };
  }, []);
  return (
    <button
      type="button"
      onClick={toggle}
      className="inline-flex items-center justify-center h-5 w-5 rounded-full hover:bg-background/80 text-foreground/70 hover:text-foreground"
      title={playing ? "Stop" : "Preview this clip"}
    >
      {playing ? <Pause size={11} /> : <Play size={11} />}
    </button>
  );
}

function KindTabs({
  value,
  onChange,
}: {
  value: AssetKind;
  onChange: (k: AssetKind) => void;
}) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
      {KIND_TABS.map((t) => {
        const active = value === t.id;
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => onChange(t.id)}
            className={`p-3 rounded-md border text-left flex items-center gap-3 ${
              active
                ? "border-primary bg-primary/10"
                : "border-border hover:bg-muted/50"
            }`}
          >
            <t.Icon size={20} className={active ? "text-primary" : "text-muted-foreground"} />
            <div>
              <div className="font-display uppercase tracking-tight text-sm">
                {t.label}
              </div>
              <div className="text-[10px] text-muted-foreground">{t.hint}</div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function GeneratePanel({ kind }: { kind: AssetKind }) {
  const [name, setName] = useState("");
  // Per-kind sticky prompt. Each asset kind (scene/board/bag)
  // remembers its own last-edited prompt across tab switches AND
  // across page reloads, so an in-progress idea never gets clobbered
  // when the admin pops over to another kind to glance at it.
  // Stored as a single { kind: prompt } object under one localStorage
  // key so adding a new kind later doesn't fragment storage.
  const [perKindPrompts, setPerKindPrompts] = useStickyState<
    Partial<Record<AssetKind, string>>
  >("design.lastPrompts", {});
  const prompt = perKindPrompts[kind] ?? DEFAULT_PROMPTS[kind];
  const setPrompt = (next: string) => {
    setPerKindPrompts({ ...perKindPrompts, [kind]: next });
  };
  const [error, setError] = useState<string | null>(null);
  // SFX clip length. null = let ElevenLabs choose a natural length;
  // otherwise a fixed 0.5–10s. Sticky so a returning admin keeps their
  // preference.
  const [sfxDuration, setSfxDuration] = useStickyState<number | null>(
    "design.sfxDuration",
    null,
  );
  const gen = useGenerateAsset();
  const genSfx = useGenerateSfx();
  const upload = useUploadAsset();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { data: status } = useAssetStatus();
  const canGenerate = status?.hasGeminiKey && status?.hasR2;
  // SFX generation uses ElevenLabs, not Gemini.
  const canGenerateSfx = status?.hasElevenLabsKey && status?.hasR2;
  const canUpload = status?.hasR2; // upload doesn't need an AI key

  // Prompt history: dedup'd list of every prompt the admin has used
  // for THIS kind, newest first. Server-backed (lives on the assets
  // table) so it survives across devices and browser resets. Up to
  // 10 chips surfaced for quick re-use; click fills the textarea.
  const { data: kindAssets = [] } = useAdminAssets({ kind });
  const recentPrompts = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const a of kindAssets) {
      const p = a.prompt?.trim();
      if (!p) continue;
      if (seen.has(p)) continue;
      seen.add(p);
      out.push(p);
      if (out.length >= 10) break;
    }
    return out;
  }, [kindAssets]);

  async function submit() {
    setError(null);
    try {
      await gen.mutateAsync({ kind, name: name.trim(), prompt: prompt.trim() });
      setName("");
    } catch (e) {
      setError((e as ApiError).message ?? "Generation failed");
    }
  }

  // Text → sound effect via ElevenLabs. Lands as a draft sfx clip;
  // bust the in-memory catalog so it's auditionable immediately.
  async function submitSfx() {
    setError(null);
    try {
      // SFX has no separate Name field — derive the clip name from the
      // description (admin can rename in the catalog).
      const derivedName = (name.trim() || prompt.trim()).slice(0, 80);
      await genSfx.mutateAsync({
        name: derivedName,
        prompt: prompt.trim(),
        ...(sfxDuration != null ? { durationSeconds: sfxDuration } : {}),
      });
      setName("");
      invalidateSfxCatalog();
    } catch (e) {
      setError((e as ApiError).message ?? "Sound generation failed");
    }
  }

  // Upload an admin-prepared image (e.g. a Photoshopped transparent
  // PNG). Name field doubles as the upload name; if empty, the file
  // name (sans extension) is used as a fallback.
  async function onFileChosen(file: File) {
    setError(null);
    const reader = new FileReader();
    const dataUrl = await new Promise<string>((resolve, reject) => {
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    }).catch((e) => {
      setError((e as Error).message ?? "Could not read file");
      return null;
    });
    if (!dataUrl) return;
    const effectiveName =
      name.trim() ||
      file.name.replace(/\.[a-z0-9]+$/i, "").slice(0, 80) ||
      "Untitled";
    try {
      await upload.mutateAsync({ kind, name: effectiveName, dataUrl });
      setName("");
      if (fileInputRef.current) fileInputRef.current.value = "";
      // New SFX clip won't appear in the game until the in-memory
      // catalog re-fetches. Bust it eagerly so the admin can hear it
      // immediately on the next round-start (etc.) test.
      if (kind === "sfx") invalidateSfxCatalog();
    } catch (e) {
      setError((e as ApiError).message ?? "Upload failed");
    }
  }

  const isSfx = kind === "sfx";

  return (
    <Card>
      <CardHeader>
        <CardTitle className="capitalize">
          {isSfx ? "Generate sound effect" : `Generate ${kind}`}
        </CardTitle>
        <CardDescription>
          {isSfx
            ? "Describe the sound and generate it (ElevenLabs). Short, punchy game one-shots work best. Or upload your own MP3/WAV/OGG. New clips land as drafts — preview, flip to active, then assign them to an event in Game Sounds below."
            : "Recipe is pre-pended automatically (top-down, no perspective, no baked-in hole for boards, etc.) so the output drops straight into the renderer."}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* SFX = ONE field. The old Name + Prompt split caused people
            to type their description into Name and unknowingly generate
            the leftover default prompt. Now the description IS the only
            input; the clip name is auto-derived from it (rename later
            in the catalog). */}
        <div className={isSfx ? "" : "grid sm:grid-cols-[1fr_2fr] gap-3"}>
          {!isSfx && (
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">
                Name
              </label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={
                  kind === "bag"
                    ? "Camo Duckcloth"
                    : kind === "board"
                      ? "Barnwood Reclaimed"
                      : "Sunset Backyard"
                }
                maxLength={80}
              />
            </div>
          )}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">
              {isSfx ? "Describe the sound to generate" : "Prompt"}
            </label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={2}
              placeholder={
                isSfx
                  ? "e.g. outdoor crowd applause with whistles, like a sporting event"
                  : undefined
              }
              className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            />
            {recentPrompts.length > 0 && (
              <PromptHistory
                prompts={recentPrompts}
                onPick={(p) => setPrompt(p)}
              />
            )}
          </div>
        </div>
        {isSfx && (
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-muted-foreground">
              Length
            </label>
            <select
              value={sfxDuration ?? ""}
              onChange={(e) =>
                setSfxDuration(e.target.value ? Number(e.target.value) : null)
              }
              className="h-8 rounded-md border border-input bg-transparent px-2 text-xs"
              title="Fixed clip length, or Auto to let the model choose"
            >
              <option value="">Auto (model decides)</option>
              <option value="0.5">0.5 s</option>
              <option value="1">1 s</option>
              <option value="1.5">1.5 s</option>
              <option value="2">2 s</option>
              <option value="3">3 s</option>
              <option value="5">5 s</option>
              <option value="10">10 s</option>
            </select>
            <span className="text-[11px] text-muted-foreground">
              Game one-shots are best ≤2 s. Only applies to Generate (not
              Upload).
            </span>
          </div>
        )}
        {error && <p className="text-sm text-destructive">{error}</p>}
        <div className="flex flex-wrap justify-end gap-2 items-center">
          {/* Upload path — drop a hand-keyed transparent PNG (or any
              image) without going through Gemini. Renderer auto-
              detects source alpha, so transparent PNGs skip the
              white-key and display cleanly. SFX kind switches the
              file-picker accept list to audio mimes. */}
          <input
            ref={fileInputRef}
            type="file"
            accept={
              isSfx
                ? "audio/mpeg,audio/mp3,audio/wav,audio/x-wav,audio/ogg"
                : "image/png,image/jpeg,image/webp"
            }
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void onFileChosen(f);
            }}
          />
          <Button
            type="button"
            variant={isSfx ? "default" : "outline"}
            onClick={() => fileInputRef.current?.click()}
            disabled={upload.isPending || !canUpload}
            title={
              isSfx
                ? "Upload an audio clip (MP3/WAV/OGG, ≤9 MB)"
                : "Upload a hand-prepared image (PNG/JPEG/WebP, ≤9 MB)"
            }
          >
            {upload.isPending ? (
              <>
                <Loader2 size={14} className="animate-spin" /> Uploading…
              </>
            ) : (
              <>
                <Upload size={14} /> Upload
              </>
            )}
          </Button>
          {isSfx ? (
            <Button
              onClick={submitSfx}
              disabled={
                genSfx.isPending ||
                !canGenerateSfx ||
                prompt.trim().length < 3
              }
              title={
                canGenerateSfx
                  ? "Generate this sound with ElevenLabs"
                  : "Set ELEVENLABS_API_KEY on the server to enable AI sound generation"
              }
            >
              {genSfx.isPending ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Generating…
                </>
              ) : (
                <>
                  <Sparkles size={14} /> Generate
                </>
              )}
            </Button>
          ) : (
            <Button
              onClick={submit}
              disabled={
                gen.isPending ||
                !canGenerate ||
                name.trim().length === 0 ||
                prompt.trim().length < 3
              }
            >
              {gen.isPending ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Generating…
                </>
              ) : (
                <>
                  <Sparkles size={14} /> Generate
                </>
              )}
            </Button>
          )}
        </div>
        {isSfx && !canGenerateSfx && status && (
          <p className="text-xs text-muted-foreground">
            AI sound generation is off — set{" "}
            <code className="bg-muted px-1 py-0.5 rounded">
              ELEVENLABS_API_KEY
            </code>{" "}
            on the server (<code className="bg-muted px-1 py-0.5 rounded">
              flyctl secrets set
            </code>) to enable it. You can still upload clips.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// Horizontal scrollable strip of recent prompt chips, newest first.
// Click fills the prompt textarea with the chosen text. Full prompt
// in title tooltip so the admin can read it before picking.
// Compact select dropdown — fixed height, doesn't push other UI
// around the way a row of chips did. Picking an entry fills the
// prompt textarea; selecting the placeholder ("Recent prompts…") is
// a no-op. The native <select> truncates long entries automatically
// and shows the full string in its open menu.
function PromptHistory({
  prompts,
  onPick,
}: {
  prompts: string[];
  onPick: (prompt: string) => void;
}) {
  return (
    <select
      aria-label="Recent prompts"
      value=""
      onChange={(e) => {
        const v = e.target.value;
        if (v) onPick(v);
        // Reset so the user can re-pick the same prompt later.
        e.currentTarget.value = "";
      }}
      className="mt-1 w-full text-xs h-8 rounded-md border border-input bg-transparent px-2 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
    >
      <option value="">Recent prompts ({prompts.length})…</option>
      {prompts.map((p) => (
        <option key={p} value={p}>
          {p.length > 90 ? `${p.slice(0, 87)}…` : p}
        </option>
      ))}
    </select>
  );
}

function CatalogPanel({
  kind,
  statusFilter,
  setStatusFilter,
  search,
  setSearch,
}: {
  kind: AssetKind;
  statusFilter: AssetStatus | "all";
  setStatusFilter: (s: AssetStatus | "all") => void;
  search: string;
  setSearch: (s: string) => void;
}) {
  const { data: rows = [], isLoading, error } = useAdminAssets({
    kind,
    status: statusFilter,
  });
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        (r.prompt ?? "").toLowerCase().includes(q) ||
        (r.slug ?? "").toLowerCase().includes(q),
    );
  }, [rows, search]);

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle className="capitalize">{kind} catalog</CardTitle>
          <div className="text-xs text-muted-foreground">
            {filtered.length} of {rows.length}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 items-center mt-2">
          <div className="inline-flex rounded-md border border-border overflow-hidden text-xs font-display uppercase tracking-widest">
            {STATUS_TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setStatusFilter(t.id)}
                className={`px-3 py-1.5 ${
                  statusFilter === t.id
                    ? "bg-foreground text-background"
                    : "text-muted-foreground hover:bg-muted/50"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="relative flex-1 min-w-[180px]">
            <Search
              size={14}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, prompt, slug…"
              className="pl-8 h-9 text-sm"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {error ? (
          <p className="p-6 text-sm text-destructive">
            Could not load catalog: {(error as ApiError).message}
          </p>
        ) : isLoading ? (
          <p className="p-6 text-sm text-muted-foreground">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground">
            No {kind}s match the current filter.
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {filtered.map((row) => (
              <AssetRow key={row.id} asset={row} />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function AssetRow({ asset }: { asset: Asset }) {
  const upd = useUpdateAsset();
  const del = useDeleteAsset();
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(asset.name);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState(false);
  const [aligning, setAligning] = useState(false);

  // Reset the local draft whenever the upstream row updates (e.g. an
  // optimistic invalidate + refetch lands a renamed value).
  useEffect(() => {
    setNameDraft(asset.name);
  }, [asset.name]);

  async function setStatus(next: AssetStatus) {
    if (next === asset.status) return;
    setError(null);
    try {
      await upd.mutateAsync({ id: asset.id, status: next });
      // SFX active/inactive flips change what the renderer plays.
      // Bust the in-memory catalog so the next slot fire re-fetches.
      if (asset.kind === "sfx") invalidateSfxCatalog();
    } catch (e) {
      setError((e as ApiError).message ?? "Could not update status");
    }
  }

  async function saveName() {
    const trimmed = nameDraft.trim();
    setEditingName(false);
    if (trimmed === asset.name || trimmed.length === 0) {
      setNameDraft(asset.name);
      return;
    }
    setError(null);
    try {
      await upd.mutateAsync({ id: asset.id, name: trimmed });
    } catch (e) {
      setError((e as ApiError).message ?? "Could not rename");
      setNameDraft(asset.name);
    }
  }

  async function destroy() {
    if (asset.isBuiltIn) return;
    if (!confirm(`Delete "${asset.name}"? This removes the R2 object too.`)) {
      return;
    }
    setError(null);
    try {
      await del.mutateAsync(asset.id);
      if (asset.kind === "sfx") invalidateSfxCatalog();
    } catch (e) {
      setError((e as ApiError).message ?? "Could not delete");
    }
  }

  const swatch = asset.swatchHex
    ? `#${asset.swatchHex.toString(16).padStart(6, "0")}`
    : null;
  const isAudio = asset.kind === "sfx" || asset.kind === "music";
  const canPreview = !isAudio && !!asset.imageUrl;

  return (
    <li className="p-3 sm:p-4 flex flex-wrap sm:flex-nowrap items-center gap-3">
      {/* Thumbnail / swatch / audio play — for sfx + music rows the
          square becomes a play button instead of an image preview. */}
      {isAudio ? (
        <SfxPlayButton url={asset.imageUrl} label={asset.name} />
      ) : (
        <button
          type="button"
          onClick={() => canPreview && setPreview(true)}
          disabled={!canPreview}
          className="w-16 h-16 rounded-md bg-muted border border-border flex items-center justify-center overflow-hidden shrink-0 disabled:cursor-default enabled:cursor-zoom-in enabled:hover:ring-2 enabled:hover:ring-primary/30 transition"
          title={canPreview ? "Click to preview" : undefined}
          aria-label={canPreview ? `Preview ${asset.name}` : asset.name}
        >
          {asset.imageUrl ? (
            <img
              src={asset.imageUrl}
              alt={asset.name}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : swatch ? (
            <div
              className="w-full h-full"
              style={{ backgroundColor: swatch }}
              aria-label={`swatch ${swatch}`}
            />
          ) : (
            <ImageIcon size={20} className="text-muted-foreground" />
          )}
        </button>
      )}

      {preview && asset.imageUrl && (
        <ImagePreviewModal
          src={asset.imageUrl}
          alt={asset.name}
          caption={asset.name}
          onClose={() => setPreview(false)}
        />
      )}

      {/* Name + meta */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          {editingName ? (
            <input
              autoFocus
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              onBlur={saveName}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  saveName();
                }
                if (e.key === "Escape") {
                  setNameDraft(asset.name);
                  setEditingName(false);
                }
              }}
              className="font-medium bg-transparent border-b border-primary outline-none px-0 py-0.5 min-w-0 flex-1"
              maxLength={80}
            />
          ) : (
            <button
              type="button"
              onClick={() => setEditingName(true)}
              className="font-medium text-left hover:underline truncate inline-flex items-center gap-1.5 group"
              title="Click to rename"
            >
              <span className="truncate">{asset.name}</span>
              <Pencil
                size={11}
                className="text-muted-foreground opacity-0 group-hover:opacity-100 transition shrink-0"
                aria-hidden
              />
            </button>
          )}
          {asset.isBuiltIn && (
            <span className="text-[9px] font-display uppercase tracking-widest px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
              Built-in
            </span>
          )}
        </div>
        <div className="text-xs text-muted-foreground truncate">
          {asset.slug && (
            <code className="bg-muted/60 px-1 py-0.5 rounded mr-2">
              {asset.slug}
            </code>
          )}
          {asset.prompt ?? <em>seeded preset</em>}
        </div>
        {error && (
          <p className="text-xs text-destructive mt-0.5">{error}</p>
        )}
      </div>

      {/* SFX rows show a slot picker (which named effect this audio
          plays for) instead of the alignment button. Changing the
          slot updates the row's `slug` column server-side; the
          renderer re-fetches the catalog on next play. */}
      {asset.kind === "sfx" && (
        <SfxSlotPicker
          slug={asset.slug as SfxSlot | null}
          onChange={async (next) => {
            try {
              await upd.mutateAsync({ id: asset.id, slug: next });
              invalidateSfxCatalog();
            } catch (e) {
              setError((e as ApiError).message ?? "Could not update slot");
            }
          }}
          pending={upd.isPending}
        />
      )}

      {/* Per-asset texture alignment. Only meaningful for bags with
          a generated/uploaded image — the modal lets the admin frame
          the photo to the cap face and persists the offset/zoom/
          rotation back to asset.metadata. Every render path
          (in-game finger bag, pile, in-flight, opponent magic-launch)
          reads from these same fields. */}
      {(asset.kind === "bag" || asset.kind === "scene") && asset.imageUrl && (
        <button
          type="button"
          onClick={() => setAligning(true)}
          className="text-xs font-display uppercase tracking-wider px-2 py-1 rounded border border-border hover:border-primary hover:text-primary transition shrink-0"
          title={
            asset.kind === "bag"
              ? "Align the generated image to the bag cap"
              : "Frame the scene against the game viewport"
          }
        >
          Align
        </button>
      )}

      {/* key={asset.updatedAt} forces the modal to remount whenever
          React Query refetches new metadata — guarantees the initial
          useState calls inside the modal read the freshest saved
          values, not a snapshot from before Save. */}
      {aligning && asset.kind === "bag" && (
        <AlignBagModal
          key={asset.updatedAt}
          asset={asset}
          onClose={() => setAligning(false)}
        />
      )}
      {aligning && asset.kind === "scene" && (
        <AlignSceneModal
          key={asset.updatedAt}
          asset={asset}
          onClose={() => setAligning(false)}
        />
      )}

      <StatusMenu
        value={asset.status}
        pending={upd.isPending}
        onChange={setStatus}
      />

      {/* Delete */}
      <button
        type="button"
        onClick={destroy}
        disabled={asset.isBuiltIn || del.isPending}
        className="text-muted-foreground hover:text-destructive disabled:opacity-30 disabled:hover:text-muted-foreground shrink-0 p-1"
        title={
          asset.isBuiltIn
            ? "Built-ins can be deactivated but not deleted"
            : "Delete"
        }
      >
        <Trash2 size={14} />
      </button>
    </li>
  );
}

// Explicit status menu: pill shows current state, clicking opens a
// small popover with all three options. Each choice fires an update;
// the menu closes immediately. Replaces the previous "click to cycle"
// pill, which made it ambiguous whether your click had landed.
function StatusMenu({
  value,
  pending,
  onChange,
}: {
  value: AssetStatus;
  pending: boolean;
  onChange: (s: AssetStatus) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside-click / Escape.
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const classes: Record<AssetStatus, string> = {
    active:
      "bg-green-500/10 border-green-500/30 text-green-700 dark:text-green-300",
    draft:
      "bg-amber-500/10 border-amber-500/30 text-amber-700 dark:text-amber-300",
    inactive: "bg-muted text-muted-foreground border-border",
  };

  return (
    <div ref={ref} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={pending}
        className={`text-[10px] font-display uppercase tracking-widest pl-2.5 pr-1.5 py-1 rounded-full border inline-flex items-center gap-1 ${classes[value]} disabled:opacity-60`}
        title="Change status"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {pending ? (
          <Loader2 size={11} className="animate-spin" />
        ) : (
          <>
            <span>{value}</span>
            <ChevronDown size={11} aria-hidden />
          </>
        )}
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-1 z-20 min-w-[9rem] rounded-md border border-border bg-popover text-popover-foreground shadow-md p-1"
        >
          {(["draft", "active", "inactive"] as const).map((s) => (
            <button
              key={s}
              type="button"
              role="menuitemradio"
              aria-checked={value === s}
              onClick={() => {
                setOpen(false);
                onChange(s);
              }}
              className={`w-full text-left text-xs font-medium px-2.5 py-1.5 rounded inline-flex items-center justify-between gap-2 hover:bg-muted ${
                value === s ? "text-foreground" : "text-muted-foreground"
              }`}
            >
              <span className="inline-flex items-center gap-2">
                <span
                  className={`inline-block w-2 h-2 rounded-full ${
                    s === "active"
                      ? "bg-green-500"
                      : s === "draft"
                        ? "bg-amber-500"
                        : "bg-muted-foreground/50"
                  }`}
                  aria-hidden
                />
                <span className="capitalize">{s}</span>
              </span>
              {value === s && <Check size={12} aria-hidden />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Inline audio preview for sfx rows. Tap to play/pause the uploaded
// clip. We use a single shared Audio element per mount so an admin
// can sample a clip without dragging the rest of the row open.
function SfxPlayButton({
  url,
  label,
}: {
  url: string | null;
  label: string;
}) {
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Lazy-construct the Audio on first play. Releases the network /
  // decode cost until the admin actually wants to hear the clip.
  function toggle() {
    if (!url) return;
    if (!audioRef.current) {
      audioRef.current = new Audio(url);
      audioRef.current.addEventListener("ended", () => setPlaying(false));
      audioRef.current.addEventListener("pause", () => setPlaying(false));
      audioRef.current.addEventListener("play", () => setPlaying(true));
    }
    const a = audioRef.current;
    if (playing) {
      a.pause();
    } else {
      a.currentTime = 0;
      void a.play().catch(() => setPlaying(false));
    }
  }

  // Stop playback on unmount so a row vanishing mid-clip doesn't keep
  // sounding from a detached audio element.
  useEffect(() => {
    return () => {
      const a = audioRef.current;
      if (a) {
        a.pause();
        audioRef.current = null;
      }
    };
  }, []);

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={!url}
      className="w-16 h-16 rounded-md bg-muted border border-border flex items-center justify-center shrink-0 disabled:opacity-50 hover:bg-muted/70 transition"
      title={url ? (playing ? `Pause ${label}` : `Play ${label}`) : "No audio"}
      aria-label={url ? (playing ? `Pause ${label}` : `Play ${label}`) : "No audio"}
    >
      {playing ? <Pause size={22} /> : <Play size={22} />}
    </button>
  );
}

// Dropdown that binds an sfx row to a named slot (one of SFX_SLOTS).
// Choosing "Unassigned" clears the slug — the renderer falls back to
// the synthesized version for that effect. Built-in rows hide the
// picker entirely (server rejects slug changes on them).
function SfxSlotPicker({
  slug,
  onChange,
  pending,
}: {
  slug: SfxSlot | null;
  onChange: (next: string | null) => void;
  pending: boolean;
}) {
  return (
    <label className="text-xs shrink-0 inline-flex items-center gap-1.5">
      <span className="font-display uppercase tracking-widest text-[10px] text-muted-foreground">
        Slot
      </span>
      <select
        value={slug ?? ""}
        disabled={pending}
        onChange={(e) => onChange(e.target.value || null)}
        className="h-8 rounded-md border border-input bg-transparent px-2 text-xs disabled:opacity-60"
      >
        <option value="">Unassigned</option>
        {SFX_SLOTS.map((s) => (
          <option key={s} value={s}>
            {SFX_SLOT_LABELS[s]}
          </option>
        ))}
      </select>
    </label>
  );
}

// Full-screen image lightbox. Click backdrop or X to close.
// Escape also closes. Image scales to fit the viewport.
function ImagePreviewModal({
  src,
  alt,
  caption,
  onClose,
}: {
  src: string;
  alt: string;
  caption?: string;
  onClose: () => void;
}) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    // Lock body scroll while the lightbox is open.
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Preview: ${caption ?? alt}`}
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute top-3 right-3 sm:top-6 sm:right-6 text-white/80 hover:text-white p-2 rounded-full bg-black/40 hover:bg-black/60"
        aria-label="Close preview"
      >
        <X size={18} />
      </button>
      <figure
        className="flex flex-col items-center gap-3 max-w-full max-h-full"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={src}
          alt={alt}
          className="max-w-full max-h-[85vh] object-contain rounded-md shadow-2xl"
        />
        {caption && (
          <figcaption className="text-sm text-white/80 font-medium">
            {caption}
          </figcaption>
        )}
      </figure>
    </div>
  );
}

// Per-bag texture alignment.
//
// Goal: line up the generated image so the bag part of the photo fills
// the cap face. Identity (zoom=1, no pan/rotate) wraps the entire
// image edge-to-edge across the rounded-corner square that is the bag
// top. After alignment, the values are baked into the BagAsset's
// metadata and every render path (player finger bag, pile, in-flight,
// opponent magic-launch) reads from there.
//
// The preview frame is a 1:1 square showing what will appear on the
// cap. Image content OUTSIDE the dashed cap outline is cropped on the
// real bag. The cap shape is approximated with rounded corners
// matching makeBagGeometry's ~13% corner radius.
function AlignBagModal({
  asset,
  onClose,
}: {
  asset: Asset;
  onClose: () => void;
}) {
  const upd = useUpdateAsset();
  const meta = asset.metadata as {
    texOffsetU?: number;
    texOffsetV?: number;
    texScale?: number;
    texRotationDeg?: number;
    texFlipU?: boolean;
    texFlipV?: boolean;
  };

  // Local edit state. Initialized from the asset's saved values (or
  // the identity transform when this is the first alignment pass).
  // Save only writes when something actually changed.
  const [offsetU, setOffsetU] = useState(meta.texOffsetU ?? 0);
  const [offsetV, setOffsetV] = useState(meta.texOffsetV ?? 0);
  const [scale, setScale] = useState(meta.texScale ?? 1);
  const [rotDeg, setRotDeg] = useState(meta.texRotationDeg ?? 0);
  const [flipU, setFlipU] = useState(meta.texFlipU ?? false);
  const [flipV, setFlipV] = useState(meta.texFlipV ?? false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  function reset() {
    setOffsetU(0);
    setOffsetV(0);
    setScale(1);
    setRotDeg(0);
    setFlipU(false);
    setFlipV(false);
  }

  async function save() {
    setError(null);
    try {
      await upd.mutateAsync({
        id: asset.id,
        metadata: {
          texOffsetU: offsetU,
          texOffsetV: offsetV,
          texScale: scale,
          texRotationDeg: rotDeg,
          texFlipU: flipU,
          texFlipV: flipV,
        },
      });
      onClose();
    } catch (e) {
      setError((e as ApiError).message ?? "Could not save alignment");
    }
  }

  // CSS transform that mirrors three.js's tex.offset/repeat/rotation
  // around the texture center.
  //
  // Three.js applies offset AFTER repeat in the texture matrix, so
  // shifting `offsetU` by 0.1 at scale=2 moves the visible window by
  // 0.1 of the ALREADY-DOUBLED image — equivalent to 20% of the
  // container, not 10%. The CSS translate amount has to scale with
  // both offsetU and the current zoom.
  //
  // Rotation direction: three.js's tex.rotation is positive=CCW
  // (standard math), CSS rotate() is positive=CW. Negate to keep the
  // slider intuitive — slider +30 reads as a 30° CW twist in the
  // preview AND on the actual bag.
  // CSS transform that mirrors three.js's UV matrix at center=(0.5).
  // The image is scaled around its own center, optionally flipped
  // via negative scale, rotated, then translated by the user's pan
  // amount (scaled by zoom because translate in CSS happens in the
  // post-scale coordinate space at percentage units of the
  // pre-scaled box). Letters/logos that look right-side up in the
  // preview will read the same way on the bag.
  const fu = flipU ? -1 : 1;
  const fv = flipV ? -1 : 1;
  const previewTransform = `translate(${-offsetU * scale * 100}%, ${-offsetV * scale * 100}%) rotate(${-rotDeg}deg) scale(${fu * scale}, ${fv * scale})`;

  return (
    <div
      className="fixed inset-0 bg-background/95 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-card border border-border rounded-lg shadow-2xl w-full max-w-md max-h-[90vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div>
            <h2 className="font-display text-lg leading-tight">Align bag image</h2>
            <p className="text-xs text-muted-foreground mt-0.5 truncate max-w-[18rem]">
              {asset.name}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground p-1"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Preview frame. Outer container is a 1:1 square. The
              image is rendered full-size inside, transformed live by
              the sliders. The dashed rounded-square overlay shows the
              cap boundary — anything outside the dashes is cropped on
              the actual bag. */}
          <div className="relative w-full aspect-square bg-black/30 rounded-md overflow-hidden border border-border">
            {asset.imageUrl && (
              <img
                src={asset.imageUrl}
                alt=""
                className="absolute inset-0 w-full h-full object-cover select-none pointer-events-none"
                style={{
                  transform: previewTransform,
                  transformOrigin: "center center",
                }}
                draggable={false}
              />
            )}
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute inset-[2%] rounded-[13%] border-2 border-dashed border-primary/80 shadow-[inset_0_0_0_9999px_rgba(0,0,0,0.0)]" />
              <div className="absolute top-1 left-1 text-[10px] font-display uppercase tracking-wider text-primary/90 bg-background/60 px-1.5 py-0.5 rounded">
                Cap area
              </div>
            </div>
          </div>

          <AlignSlider
            label="Zoom"
            value={scale}
            onChange={setScale}
            min={0.3}
            max={3}
            step={0.01}
            display={`${scale.toFixed(2)}×`}
          />
          <AlignSlider
            label="Pan X"
            value={offsetU}
            onChange={setOffsetU}
            min={-0.5}
            max={0.5}
            step={0.005}
            display={offsetU.toFixed(3)}
          />
          <AlignSlider
            label="Pan Y"
            value={offsetV}
            onChange={setOffsetV}
            min={-0.5}
            max={0.5}
            step={0.005}
            display={offsetV.toFixed(3)}
          />
          <AlignSlider
            label="Rotation"
            value={rotDeg}
            onChange={setRotDeg}
            min={-180}
            max={180}
            step={1}
            display={`${rotDeg}°`}
          />

          {/* Mirror toggles. Default off matches current behavior.
              Use when the texture's logo / text reads backwards on
              the in-game bag — the 3D bag's UV remap can end up
              flipped depending on how the source image was
              authored. */}
          <div className="flex items-center justify-between gap-3 pt-1">
            <span className="text-xs font-display uppercase tracking-wider text-muted-foreground">
              Mirror
            </span>
            <div className="flex items-center gap-3 text-xs">
              <label className="inline-flex items-center gap-1.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={flipU}
                  onChange={(e) => setFlipU(e.target.checked)}
                  className="accent-primary"
                />
                <span>Horizontal</span>
              </label>
              <label className="inline-flex items-center gap-1.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={flipV}
                  onChange={(e) => setFlipV(e.target.checked)}
                  className="accent-primary"
                />
                <span>Vertical</span>
              </label>
            </div>
          </div>

          {error && (
            <p className="text-xs text-destructive">{error}</p>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 p-4 border-t border-border">
          <Button variant="ghost" size="sm" onClick={reset} disabled={upd.isPending}>
            Reset
          </Button>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={onClose} disabled={upd.isPending}>
              Cancel
            </Button>
            <Button size="sm" onClick={save} disabled={upd.isPending}>
              {upd.isPending ? (
                <>
                  <Loader2 size={14} className="animate-spin mr-1.5" />
                  Saving…
                </>
              ) : (
                "Save alignment"
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AlignSlider({
  label,
  value,
  onChange,
  min,
  max,
  step,
  display,
}: {
  label: string;
  value: number;
  onChange: (next: number) => void;
  min: number;
  max: number;
  step: number;
  display: string;
}) {
  return (
    <label className="block">
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-xs font-display uppercase tracking-wider text-muted-foreground">
          {label}
        </span>
        <span className="text-xs tabular-nums">{display}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-primary"
      />
    </label>
  );
}

// Per-scene backdrop alignment. The actual game's scene backdrop is
// a flat plane behind the far board (see virtual3d.ts applyBackdrop
// Transform). Each scene needs its own framing because a panoramic
// backyard photo and a portrait neon-arena render at wildly
// different distances/sizes — globals don't work. This modal mounts
// a mini three.js scene with the same board placement the live game
// uses, lets the admin live-tune distance/size/zoom/pan, and
// persists those values to the scene asset's metadata. The live
// game reads from there with hardcoded defaults as fallback.
//
// Board placement IS global (1.22 × 2.44m slab at z=7.5, raked ~10°,
// matches createBoard in virtual3d.ts). We don't expose those knobs
// here — the scene tunes ITSELF against the consistent board.
function AlignSceneModal({
  asset,
  onClose,
}: {
  asset: Asset;
  onClose: () => void;
}) {
  const upd = useUpdateAsset();
  const meta = asset.metadata as {
    backdropDistanceZ?: number;
    backdropOffsetX?: number;
    backdropOffsetY?: number;
    backdropWidth?: number;
    backdropHeight?: number;
    backdropScale?: number;
    backdropOffsetU?: number;
    backdropOffsetV?: number;
  };

  // Local edit state. Saved-or-default seed values match the
  // renderer's defaults in virtual3d.ts (applyBackdropTransform).
  const [distZ, setDistZ] = useState(meta.backdropDistanceZ ?? 22);
  // Whole-backdrop translation in world space. offX moves it left/
  // right, offY up/down — distinct from the texture pan (texU/texV)
  // which slides the IMAGE inside a fixed plane.
  const [offX, setOffX] = useState(meta.backdropOffsetX ?? 0);
  const [offY, setOffY] = useState(meta.backdropOffsetY ?? 4);
  const [width, setWidth] = useState(meta.backdropWidth ?? 50);
  // Default height 40 + offY 4 makes the backdrop span world y from
  // -16 to +24, well outside the camera's vertical FOV at z=22
  // (~y=-14..+10). No more procedural-sky strip showing below.
  const [height, setHeight] = useState(meta.backdropHeight ?? 40);
  const [texScale, setTexScale] = useState(meta.backdropScale ?? 1);
  const [texU, setTexU] = useState(meta.backdropOffsetU ?? 0);
  const [texV, setTexV] = useState(meta.backdropOffsetV ?? 0);
  const [error, setError] = useState<string | null>(null);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  // Refs to the three.js objects we update on slider changes — held
  // outside React state so we don't have to re-render the whole
  // scene every keystroke.
  const backdropPlaneRef = useRef<Mesh | null>(null);
  const backdropMatRef = useRef<MeshBasicMaterial | null>(null);

  // Mount the three.js scene once. Cleanup on unmount disposes
  // geometries/materials/renderer to release GPU memory.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const renderer = new WebGLRenderer({ canvas, antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(canvas.clientWidth, canvas.clientHeight, false);

    const scene = new Scene();
    scene.background = new Color(0x88a5c0); // procedural-sky stand-in

    const camera = new PerspectiveCamera(
      55,
      canvas.clientWidth / canvas.clientHeight,
      0.1,
      100,
    );
    // Match the live game's camera so the framing the admin sees
    // here is what the player will see.
    camera.position.set(0, 1.55, -0.4);
    camera.lookAt(0, 0.3, 7.5);

    // Minimal lighting — enough for the board to read as solid.
    scene.add(new AmbientLight(0xffffff, 0.4));
    const hemi = new HemisphereLight(0xa6cdf4, 0x4e6a30, 0.6);
    scene.add(hemi);
    const sun = new DirectionalLight(0xffffff, 0.7);
    sun.position.set(6, 10, 4);
    scene.add(sun);

    // No ground plane in the aligner. The live game HIDES its
    // ground whenever a scene image is loaded (the backdrop is the
    // whole visible world), so the aligner has to match that or
    // it'd show a green strip that doesn't exist at runtime.

    // Use the REAL createBoard helper from the renderer so the
    // aligner shows the same geometry the live game does — hole
    // cutout, raked slab, legs, stripe, all at the exact world
    // positions the game uses. Without this the admin tunes the
    // backdrop against a placeholder and the saved framing reads
    // wrong in the actual game (the previous BoxGeometry stand-in
    // was at the wrong height, missed the hole + legs, and didn't
    // include the near board at all).
    const dims = dimsFromAsset(REFERENCE_BOARD_TEMPLATE);
    const farBoard: BoardHandle = createBoard(REFERENCE_BOARD_TEMPLATE, dims);
    farBoard.group.position.set(0, 0, 7.5);
    scene.add(farBoard.group);
    const nearBoard: BoardHandle = createBoard(REFERENCE_BOARD_TEMPLATE, dims);
    nearBoard.group.position.set(0, 0, 0.8);
    nearBoard.group.rotation.y = Math.PI;
    scene.add(nearBoard.group);

    // Backdrop plane. Texture loads async; transform applies live
    // via the second useEffect.
    const backdropMat = new MeshBasicMaterial({
      color: 0xffffff,
      side: DoubleSide,
    });
    const backdrop = new Mesh(new PlaneGeometry(1, 1), backdropMat);
    backdrop.visible = false;
    scene.add(backdrop);
    backdropPlaneRef.current = backdrop;
    backdropMatRef.current = backdropMat;

    if (asset.imageUrl) {
      // Use the renderer's hardened loader, NOT a raw TextureLoader.
      // The catalog shows this same URL as a plain <img> thumbnail
      // (no crossOrigin), which caches an opaque/no-CORS response under
      // R2's 1-year immutable cache. A raw crossOrigin="anonymous" load
      // then reads that poisoned cache, the CORS check fails, and the
      // load errors silently — leaving the backdrop blank (the bug
      // here). loadImageTexture cache-busts past it (and resolves null
      // instead of throwing on a genuine failure).
      void loadImageTexture(asset.imageUrl).then((tex) => {
        if (!tex) return;
        backdropMat.map = tex;
        backdropMat.needsUpdate = true;
        backdrop.visible = true;
        // The live transform effect reads backdropMatRef on its next
        // run; nudge it by re-applying the current texture transform.
        backdropMatRef.current = backdropMat;
      });
    }

    let raf = 0;
    function tick() {
      renderer.render(scene, camera);
      raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      renderer.dispose();
      disposeObject(farBoard.group);
      disposeObject(nearBoard.group);
      backdropMat.dispose();
      if (backdropMat.map) backdropMat.map.dispose();
      backdrop.geometry.dispose();
      backdropPlaneRef.current = null;
      backdropMatRef.current = null;
    };
  }, [asset.imageUrl]);

  // Apply current slider values to the backdrop every time a knob
  // changes. The render loop in the mount effect picks up the
  // mutated transforms on the next frame.
  useEffect(() => {
    const backdrop = backdropPlaneRef.current;
    const mat = backdropMatRef.current;
    if (!backdrop || !mat) return;
    backdrop.position.set(offX, offY, distZ);
    backdrop.scale.set(width, height, 1);
    const tex = mat.map;
    if (tex) {
      const s = Math.max(0.05, texScale);
      tex.center.set(0.5, 0.5);
      tex.repeat.set(1 / s, 1 / s);
      tex.offset.set(0.5 - 0.5 / s + texU, 0.5 - 0.5 / s + texV);
      tex.needsUpdate = true;
    }
  }, [distZ, offX, offY, width, height, texScale, texU, texV]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  function reset() {
    setDistZ(22);
    setOffX(0);
    setOffY(4);
    setWidth(50);
    setHeight(40);
    setTexScale(1);
    setTexU(0);
    setTexV(0);
  }

  async function save() {
    setError(null);
    try {
      await upd.mutateAsync({
        id: asset.id,
        metadata: {
          backdropDistanceZ: distZ,
          backdropOffsetX: offX,
          backdropOffsetY: offY,
          backdropWidth: width,
          backdropHeight: height,
          backdropScale: texScale,
          backdropOffsetU: texU,
          backdropOffsetV: texV,
        },
      });
      onClose();
    } catch (e) {
      setError((e as ApiError).message ?? "Could not save alignment");
    }
  }

  return (
    <div
      className="fixed inset-0 bg-background/95 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-card border border-border rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div>
            <h2 className="font-display text-lg leading-tight">Align scene</h2>
            <p className="text-xs text-muted-foreground mt-0.5 truncate max-w-[22rem]">
              {asset.name}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground p-1"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Live 3D viewport. The board sits where it does in the
              actual game so the admin frames the backdrop against
              consistent geometry. */}
          <div className="relative w-full bg-black rounded-md overflow-hidden border border-border">
            <canvas
              ref={canvasRef}
              className="w-full block"
              style={{ aspectRatio: "4 / 3" }}
            />
            <div className="absolute top-1 left-1 text-[10px] font-display uppercase tracking-wider text-primary/90 bg-background/60 px-1.5 py-0.5 rounded">
              Live game view
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
            <AlignSlider
              label="Distance"
              value={distZ}
              onChange={setDistZ}
              min={10}
              max={50}
              step={0.5}
              display={`${distZ.toFixed(1)} m`}
            />
            <AlignSlider
              label="Horizontal offset"
              value={offX}
              onChange={setOffX}
              min={-25}
              max={25}
              step={0.25}
              display={`${offX.toFixed(2)} m`}
            />
            <AlignSlider
              label="Vertical offset"
              value={offY}
              onChange={setOffY}
              min={-15}
              max={20}
              step={0.25}
              display={`${offY.toFixed(2)} m`}
            />
            <AlignSlider
              label="Width"
              value={width}
              onChange={setWidth}
              min={10}
              max={120}
              step={1}
              display={`${width} m`}
            />
            <AlignSlider
              label="Height"
              value={height}
              onChange={setHeight}
              min={5}
              max={80}
              step={1}
              display={`${height} m`}
            />
            <AlignSlider
              label="Image zoom"
              value={texScale}
              onChange={setTexScale}
              min={0.3}
              max={3}
              step={0.05}
              display={`${texScale.toFixed(2)}×`}
            />
            <AlignSlider
              label="Pan X"
              value={texU}
              onChange={setTexU}
              min={-0.5}
              max={0.5}
              step={0.01}
              display={texU.toFixed(2)}
            />
            <AlignSlider
              label="Pan Y"
              value={texV}
              onChange={setTexV}
              min={-0.5}
              max={0.5}
              step={0.01}
              display={texV.toFixed(2)}
            />
          </div>

          {error && <p className="text-xs text-destructive">{error}</p>}
        </div>

        <div className="flex items-center justify-between gap-2 p-4 border-t border-border">
          <Button variant="ghost" size="sm" onClick={reset} disabled={upd.isPending}>
            Reset
          </Button>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={onClose} disabled={upd.isPending}>
              Cancel
            </Button>
            <Button size="sm" onClick={save} disabled={upd.isPending}>
              {upd.isPending ? (
                <>
                  <Loader2 size={14} className="animate-spin mr-1.5" />
                  Saving…
                </>
              ) : (
                "Save framing"
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
